import { Map } from "mapbox-gl";
import { GIS } from "../RobotMap";
import { useSelector } from "react-redux";
import { useEffect, useRef } from "react";
import {
  parseAsArrayOf,
  parseAsBoolean,
  parseAsFloat,
  parseAsString,
  useQueryState,
  useQueryStates,
} from "nuqs";
import {
  CLEANING_DIALOG_QUERY_PARAM,
  CLEANING_SUCCESS_DIALOG_QUERY_PARAM,
  MAP_FLYTO_LAT_QUERY_PARAM,
  MAP_FLYTO_LNG_QUERY_PARAM,
  SELECTED_ZONE_QUERY_PARAM,
} from "../../../utils/const";
import { center, points, polygon } from "@turf/turf";
import { getProfile } from "../../../utils/api";
import { useListAvailableZones } from "../../../hooks/cleaning";
import { ZoneType } from "../../../hooks/cleaning/index.type";
import { useListMap } from "../../../hooks/map";
import { triggerRipple } from "../../../routes/dashboard/components/cleaning/ZoneSelectedDialog";

export const polygonCoordinateFixer = (polygons: number[][]) => {
  // Ensure the polygon is closed by checking if the first and last points are the same
  if (
    polygons.length > 0 &&
    (polygons[0][0] !== polygons[polygons.length - 1][0] ||
      polygons[0][1] !== polygons[polygons.length - 1][1])
  ) {
    // If not, add the first point to the end of the array to close the polygon
    polygons.push(polygons[0]);
  }
  return polygons;
};

const ZONE_COLOR = {
  DEFAULT_BACKGROUND: "#FFFFFF",
  DEFAULT_BORDER: "#C9C9C9",
  DEFAULT_TEXT: "#686868",
  HOVER_BORDER: "#51AD82",
  HOVER_BACKGROUND: "#F1F8F4",
  SELECTED_BORDER: "#51AD82",
  SELECTED_BACKGROUND: "#DCEDDA",
};

type LayoutIds = {
  layerId?: string;
  outerLayoutId?: string;
  textLayerId?: string;
  textSourceId?: string;
};

export const useRenderZoneOnMap = (
  map: React.MutableRefObject<Map | null>,
  elements: React.ReactElement<GIS>[],
  show = true
) => {
  const [selectedZones, setSelectedZones] = useQueryState(
    SELECTED_ZONE_QUERY_PARAM,
    parseAsArrayOf(parseAsString, ";").withDefault([])
  );
  const [showCleaningBoard, setShowCleaningBoard] = useQueryState(
    CLEANING_DIALOG_QUERY_PARAM,
    parseAsBoolean.withDefault(false).withOptions({
      clearOnDefault: true,
    })
  );

  const isShowCreateDialogRef = useRef(false);
  isShowCreateDialogRef.current = showCleaningBoard;

  const [{ latitude, longitude }, setCoordinates] = useQueryStates(
    {
      // Use variable names that make sense in your codebase
      latitude: parseAsFloat
        .withDefault(0)
        .withOptions({ clearOnDefault: true }),
      longitude: parseAsFloat
        .withDefault(0)
        .withOptions({ clearOnDefault: true }),
    },
    {
      urlKeys: {
        // And remap them to shorter keys in the URL
        latitude: MAP_FLYTO_LAT_QUERY_PARAM,
        longitude: MAP_FLYTO_LNG_QUERY_PARAM,
      },
    }
  );

  const [isShowSuccess] = useQueryState(
    CLEANING_SUCCESS_DIALOG_QUERY_PARAM,
    parseAsBoolean.withDefault(false).withOptions({ clearOnDefault: true })
  );
  const organisation_id = getProfile()?.organisation;
  const { data, refetch } = useListAvailableZones({
    organisation_id,
  });

  const currentFloor = useSelector((state: any) => state.map.currentFloor);
  const zoneOptions: ZoneType[] = data?.data || [];

  const { data: dataListMaps } = useListMap({
    page_size: 100,
  });

  const listMaps = dataListMaps?.data || [];
  const currentMap = listMaps.find(
    (data) =>
      data.map_level.toString() === currentFloor.toString() && !!data.map_uuid
  );
  // const zoneOptions: ZoneType[] = data?.data || []

  useEffect(() => {
    refetch();
  }, [currentFloor]);

  const clearZoneLayers = () => {
    if (!zoneOptions || zoneOptions.length === 0) {
      return;
    }
    // Clear existing zones
    const existingPolygons = zoneOptions.filter(
      (item) => !!item.polygon_points && item.polygon_points.length > 0
    );

    existingPolygons.forEach((zone) => {
      const sourceId = `zone-source-${zone.uuid}`;
      const layerId = `zone-layer-${zone.uuid}`;
      const outlineLayerId = `outline-${sourceId}`;
      const textLayerId = `text-layer-${zone.uuid}`;
      const textSourceId = `text-source-${zone.uuid}`;

      if (map.current?.getSource(sourceId)) {
        map.current.removeLayer(layerId);
        map.current.removeLayer(outlineLayerId);
        map.current.removeLayer(textLayerId);
        map.current.removeSource(textSourceId);
        map.current.removeSource(sourceId);
      }
    });
  };

  const handleZoneClick = (zoneValue: string) => {
    if (isShowSuccess || !isShowCreateDialogRef.current) {
      return;
    }
    if (!selectedZones) {
      const newZones = [zoneValue];
      setSelectedZones?.(newZones);
      return;
    }
    let updatedSelectedZones = [...selectedZones];
    if (updatedSelectedZones.includes(zoneValue)) {
      // If already selected, remove it
      updatedSelectedZones = updatedSelectedZones.filter(
        (zone) => zone !== zoneValue
      );
    } else {
      // If not selected, add it
      updatedSelectedZones.push(zoneValue);
    }

    setSelectedZones?.(updatedSelectedZones);
    const findZone = zoneOptions.find((zone) => zone.uuid === zoneValue);
    if (!findZone) return;
    checkZoneSelectedRef.current(zoneValue, {
      layerId: `zone-layer-${zoneValue}`,
      outerLayoutId: `outline-zone-source-${zoneValue}`,
      textLayerId: `text-layer-${zoneValue}`,
      textSourceId: `text-source-${zoneValue}`,
    });


    const getLayers = map.current?.getSource(`zone-source-${zoneValue}`);
    if (!getLayers) {
      return;
    }

    const polygonCentroid = center(
      polygon((getLayers as any)._data.geometry.coordinates)
    ).geometry.coordinates;

    if (!map.current) return;
    triggerRipple(map.current, {
      lng: polygonCentroid[0],
      lat: polygonCentroid[1],
    });
  };

  const handleZoneClickRef = useRef<(zoneValue: string) => void>(() => {});
  handleZoneClickRef.current = (zoneValue: string) => {
    handleZoneClick(zoneValue);
  };

  const checkZoneSelectedRef = useRef<
    (zoneValue: string, dataLayer: LayoutIds) => void
  >(() => {});
  checkZoneSelectedRef.current = (zoneValue: string, dataLayer: LayoutIds) => {
    checkZoneSelected(zoneValue, dataLayer);
  };

  const checkZoneSelected = (zoneValue: string, dataLayer: LayoutIds) => {
    const isSelected = selectedZones.includes(zoneValue);

    if (!map.current) {
      return;
    }

    if (!!dataLayer.layerId) {
      map.current?.setPaintProperty(
        dataLayer.layerId,
        "fill-color",
        isSelected
          ? ZONE_COLOR.SELECTED_BACKGROUND
          : ZONE_COLOR.DEFAULT_BACKGROUND
      );
    }

    if (!!dataLayer.outerLayoutId) {
      map.current?.setPaintProperty(
        dataLayer.outerLayoutId,
        "line-color",
        isSelected ? ZONE_COLOR.SELECTED_BORDER : ZONE_COLOR.DEFAULT_BORDER
      );
    }

    if (!!dataLayer.textLayerId) {
      map.current?.setPaintProperty(
        dataLayer.textLayerId,
        "text-color",
        isSelected ? ZONE_COLOR.SELECTED_BORDER : ZONE_COLOR.DEFAULT_TEXT
      );
    }
  };

  const initZoneLayers = () => {
    // Clear existing zones
    const existingPolygons = zoneOptions.filter((item) =>
      !!item.polygon_points && !!currentMap
        ? currentMap.map_uuid === item.map
        : true && item.polygon_points.length > 0
    );

    clearZoneLayers();

    existingPolygons.forEach((zone) => {
      const sourceId = `zone-source-${zone.uuid}`;
      const layerId = `zone-layer-${zone.uuid}`;
      const outlineLayerId = `outline-${sourceId}`;
      const textSourceId = `text-source-${zone.uuid}`;
      const textLayerId = `text-layer-${zone.uuid}`;
      // Only add zones for the current floor

      // if (zone.floor.map_level !== currentFloor) return;

      const fixedPolygons = zone.polygon_points
        ? polygonCoordinateFixer(
            zone.polygon_points.map((point) => [
              point.longitude,
              point.latitude,
            ])
          )
        : [];

      const polygons = fixedPolygons.length > 0 ? [fixedPolygons] : [];

      // Add new zone polygons
      map.current?.addSource(sourceId, {
        type: "geojson",
        data: {
          type: "Feature",
          properties: {
            name: zone.area_name,
          },
          geometry: {
            type: "Polygon",
            coordinates: polygons,
          },
        },
      });

      map.current?.addSource(textSourceId, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: center(points(fixedPolygons)).geometry.coordinates,
              },
              properties: {
                name: zone.area_name,
              },
            },
          ],
        },
      });

      map.current?.addLayer({
        id: layerId,
        type: "fill",
        source: sourceId,
        layout: {},
        slot: 'top',
        paint: {
          "fill-color": selectedZones.includes(zone.uuid)
            ? ZONE_COLOR.SELECTED_BACKGROUND
            : ZONE_COLOR.DEFAULT_BACKGROUND,
          "fill-opacity": 1,
        },
      });

      map.current?.addLayer({
        id: outlineLayerId,
        type: "line",
        source: sourceId,
        layout: {},
        slot: 'top',
        paint: {
          "line-color": selectedZones.includes(zone.uuid)
            ? ZONE_COLOR.SELECTED_BORDER
            : ZONE_COLOR.DEFAULT_BORDER,
          "line-width": 4,
        },
      });

      map.current?.addLayer({
        id: textLayerId,
        type: "symbol",
        source: textSourceId,
        layout: {
          "text-field": ["get", "name"],
          "text-size": 14,
          "text-offset": [0, 0],
          "text-anchor": "center",
          "text-allow-overlap": true,
        },
        slot: 'top',
        paint: {
          "text-color": selectedZones.includes(zone.uuid)
            ? ZONE_COLOR.SELECTED_BORDER
            : ZONE_COLOR.DEFAULT_TEXT,
        },
      });

      map.current?.on("click", layerId, (e) => {
        e.preventDefault()
        handleZoneClickRef.current(zone.uuid);
      });

      map.current?.on("mouseenter", layerId, (e) => {
        e.preventDefault()
        if (isShowSuccess) {
          return;
        }
        map.current?.getCanvas().style.setProperty("cursor", "pointer");

        map.current?.setPaintProperty(
          outlineLayerId,
          "line-color",
          ZONE_COLOR.HOVER_BORDER
        );
        map.current?.setPaintProperty(
          textLayerId,
          "text-color",
          ZONE_COLOR.HOVER_BORDER
        );
        // map.current?.setPaintProperty(layerId, "fill-color", ZONE_COLOR.HOVER_BACKGROUND);

        map.current?.moveLayer(outlineLayerId);
      });

      map.current?.on("mouseleave", layerId, () => {
        map.current?.getCanvas().style.setProperty("cursor", "");
        if (isShowSuccess) {
          return;
        }
        checkZoneSelectedRef.current(zone.uuid, {
          layerId: layerId,
          outerLayoutId: outlineLayerId,
          textLayerId: textLayerId,
          textSourceId: textSourceId,
        });
      });
    });
  };

  useEffect(() => {
    if (latitude === 0 || longitude === 0 || !latitude || !longitude) {
      return;
    }

    if (!map.current) return;

    map.current.flyTo({ center: [longitude, latitude], zoom: 18 });

    setCoordinates({
      latitude: 0,
      longitude: 0,
    });
  }, [longitude, latitude]);

  useEffect(() => {
    if (!map.current || !currentFloor) return;

    if (!show) {
      clearZoneLayers();
      return;
    }

    if (map.current.isStyleLoaded()) {
      initZoneLayers();
    } else {
      map.current.on("load", () => {
        initZoneLayers();
      });
    }
    // Cleanup function to remove layers and sources when component unmounts or dependencies change
    return () => {
      clearZoneLayers();
    };
  }, [currentFloor, map, show]);

  useEffect(() => {
    if (!map.current || !currentFloor) return;
    if (!show) {
      clearZoneLayers();
      return;
    }

    if (map.current.isStyleLoaded()) {
      // initZoneLayers();

      const existingPolygons = zoneOptions.filter(
        (item) => !!item.polygon_points && item.polygon_points.length > 0
      );

      existingPolygons.forEach((zone) => {
        const sourceId = `zone-source-${zone.uuid}`;
        const layerId = `zone-layer-${zone.uuid}`;
        const outlineLayerId = `outline-${sourceId}`;
        const textLayerId = `text-layer-${zone.uuid}`;

        if (map.current?.getLayer(layerId)) {
          map.current?.setPaintProperty(
            layerId,
            "fill-color",
            selectedZones.includes(zone.uuid)
              ? ZONE_COLOR.SELECTED_BACKGROUND
              : ZONE_COLOR.DEFAULT_BACKGROUND
          );
        }

        if (map.current?.getLayer(outlineLayerId)) {
          map.current?.setPaintProperty(
            outlineLayerId,
            "line-color",
            selectedZones.includes(zone.uuid)
              ? ZONE_COLOR.SELECTED_BORDER
              : ZONE_COLOR.DEFAULT_BORDER
          );
        }

        if (map.current?.getLayer(textLayerId)) {
          map.current?.setPaintProperty(
            textLayerId,
            "text-color",
            selectedZones.includes(zone.uuid)
              ? ZONE_COLOR.SELECTED_BORDER
              : ZONE_COLOR.DEFAULT_TEXT
          );
        }
      });
    }
  }, [selectedZones]);
};
